import React, { useEffect, useState } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash2, Users, Shield, Building2, Edit, Eye, EyeOff, Key, GitBranch, UserCog } from 'lucide-react';
import { useAuth } from '../AuthContext';

const UserManagement = () => {
  const { isAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState({});

  // Branch access assignment
  const [branchAccessDialogOpen, setBranchAccessDialogOpen] = useState(false);
  const [accessSelectedIds, setAccessSelectedIds] = useState([]);

  // Role change
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [roleForm, setRoleForm] = useState({ role: 'Branch', branch_id: '' });

  const [formData, setFormData] = useState({
    branch_name: '',
    branch_id: '',
    email: '',
    password: '',
    role: 'Branch',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, branchesRes] = await Promise.all([
        api.get('/users'),
        api.get('/branches'),
      ]);
      setUsers(usersRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const userData = {
        ...formData,
        branch_id: formData.role === 'Branch' ? formData.branch_id : null,
      };

      // Find branch name from selected branch
      if (formData.role === 'Branch' && formData.branch_id) {
        const branch = branches.find(b => b.id === formData.branch_id);
        userData.branch_name = branch?.name || formData.branch_name;
      }

      await api.post('/auth/register', userData);
      toast.success('User created successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handlePasswordUpdate = async () => {
    if (!selectedUser || !newPassword) {
      toast.error('Please enter a new password');
      return;
    }

    try {
      await api.put(`/users/${selectedUser.id}/password`, { password: newPassword });
      toast.success('Password updated successfully');
      setPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    }
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const openBranchAccessDialog = (user) => {
    setSelectedUser(user);
    setAccessSelectedIds(user.allowed_branch_ids || []);
    setBranchAccessDialogOpen(true);
  };

  const toggleAccessBranch = (branchId) => {
    setAccessSelectedIds((prev) =>
      prev.includes(branchId) ? prev.filter((id) => id !== branchId) : [...prev, branchId]
    );
  };

  const saveBranchAccess = async () => {
    if (!selectedUser) return;
    try {
      await api.put(`/users/${selectedUser.id}/allowed-branches`, {
        allowed_branch_ids: accessSelectedIds,
      });
      toast.success('Branch access updated');
      setBranchAccessDialogOpen(false);
      setSelectedUser(null);
      setAccessSelectedIds([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update branch access');
    }
  };

  const openRoleDialog = (user) => {
    setSelectedUser(user);
    setRoleForm({ role: user.role || 'Branch', branch_id: user.branch_id || '' });
    setRoleDialogOpen(true);
  };

  const saveRole = async () => {
    if (!selectedUser) return;
    if (!roleForm.role) { toast.error('Please select a role'); return; }
    if (['Branch', 'Checker'].includes(roleForm.role) && !roleForm.branch_id) {
      toast.error('Please pick a home branch for this role');
      return;
    }
    try {
      await api.put(`/users/${selectedUser.id}/role`, {
        role: roleForm.role,
        branch_id: ['Branch', 'Checker'].includes(roleForm.role) ? roleForm.branch_id : null,
      });
      toast.success(`Role changed to ${roleForm.role}`);
      setRoleDialogOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change role');
    }
  };

  const toggleShowPassword = (userId) => {
    setShowPasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const resetForm = () => {
    setFormData({
      branch_name: '',
      branch_id: '',
      email: '',
      password: '',
      role: 'Branch',
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Admin':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Branch':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Checker':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'Account':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'HR':
        return 'bg-pink-100 text-pink-800 border-pink-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  if (!isAdmin() && currentUser?.role !== 'Checker') {
    return (
      <div className="p-6 text-center">
        <Shield className="w-16 h-16 mx-auto text-red-300 mb-4" />
        <h2 className="text-2xl font-semibold text-slate-700">Access Denied</h2>
        <p className="text-slate-500">Only administrators and checkers can access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="user-management-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">User Management</h1>
          <p className="text-lg text-slate-600">Manage portal users and their access</p>
        </div>

        {isAdmin() && (
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@example.com"
                  required
                  data-testid="user-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter password"
                  required
                  data-testid="user-password-input"
                />
              </div>
              <div>
                <Label htmlFor="role">Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Admin">Admin (Full Access)</SelectItem>
                    <SelectItem value="Branch">Branch / Mentor</SelectItem>
                    <SelectItem value="Checker">Checker / Maker</SelectItem>
                    <SelectItem value="Account">Account</SelectItem>
                    <SelectItem value="HR">HR (View Only)</SelectItem>
                    <SelectItem value="CRP-EP">CRP-EP (Business Plan only)</SelectItem>
                    <SelectItem value="CentreManager">Centre Manager (Inmates & Centre Staff)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(formData.role === 'Branch' || formData.role === 'Checker') && (
                <div>
                  <Label htmlFor="branch">Assign to Branch *</Label>
                  <Select
                    value={formData.branch_id}
                    onValueChange={(value) => {
                      const branch = branches.find(b => b.id === value);
                      setFormData({ 
                        ...formData, 
                        branch_id: value,
                        branch_name: branch?.name || ''
                      });
                    }}
                  >
                    <SelectTrigger data-testid="user-branch-select">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name} ({branch.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {branches.length === 0 && (
                    <p className="text-sm text-amber-600 mt-1">
                      No branches found. Please create a branch first.
                    </p>
                  )}
                </div>
              )}
              {formData.role === 'Admin' && (
                <div>
                  <Label htmlFor="branch_name">Display Name</Label>
                  <Input
                    id="branch_name"
                    value={formData.branch_name}
                    onChange={(e) => setFormData({ ...formData, branch_name: e.target.value })}
                    placeholder="e.g., Headquarters"
                    data-testid="user-display-name-input"
                  />
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white"
                  disabled={formData.role === 'Branch' && !formData.branch_id}
                  data-testid="save-user-btn"
                >
                  Create User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Branch
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Password
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Permissions
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50" data-testid={`user-row-${user.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-xs">
                        {user.email.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{user.email}</p>
                        <p className="text-xs text-slate-500">{user.branch_name || '-'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${getRoleColor(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {user.branch_id ? (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1 text-xs text-slate-600">
                          <Building2 className="w-3 h-3" />
                          {branches.find(b => b.id === user.branch_id)?.name || user.branch_name}
                        </div>
                        {user.allowed_branch_ids?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {user.allowed_branch_ids.map((bid) => (
                              <span key={bid} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700">
                                + {branches.find(b => b.id === bid)?.code || '…'}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">All Branches</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">
                        {showPasswords[user.id] 
                          ? (user.plain_password && user.plain_password !== '(set before this feature)' 
                              ? user.plain_password 
                              : <span className="text-amber-600 italic">Reset password to view</span>)
                          : '••••••••'}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleShowPassword(user.id)}
                        className="h-6 w-6 p-0 text-slate-500 hover:text-slate-700"
                        title={showPasswords[user.id] ? "Hide password" : "Show password"}
                        data-testid={`toggle-password-${user.id}`}
                      >
                        {showPasswords[user.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      </Button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {user.permissions?.map((perm) => (
                        <span
                          key={perm}
                          className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600"
                        >
                          {perm}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {user.id !== currentUser?.id && isAdmin() && (
                        <>
                          {(user.role === 'Branch' || user.role === 'CRP-EP') && user.branch_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openBranchAccessDialog(user)}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-7 w-7 p-0 relative"
                              title="Manage Branch Access"
                              data-testid={`branch-access-${user.id}`}
                            >
                              <GitBranch className="w-3.5 h-3.5" />
                              {user.allowed_branch_ids?.length > 0 && (
                                <span className="absolute -top-1 -right-1 bg-emerald-600 text-white text-[9px] rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-1">
                                  +{user.allowed_branch_ids.length}
                                </span>
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openRoleDialog(user)}
                            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 h-7 w-7 p-0"
                            title="Change Role"
                            data-testid={`change-role-${user.id}`}
                          >
                            <UserCog className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPasswordDialog(user)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7 w-7 p-0"
                            title="Change Password"
                            data-testid={`edit-password-${user.id}`}
                          >
                            <Key className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(user.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {users.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Users Yet</h3>
            <p className="text-slate-500">Create users to give access to the portal</p>
          </div>
        )}
      </div>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-emerald-700" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500">User</p>
              <p className="font-medium text-slate-900">{selectedUser?.email}</p>
              <p className="text-xs text-slate-500 mt-1">{selectedUser?.branch_name || 'Admin'}</p>
            </div>
            <div>
              <Label htmlFor="new-password">New Password *</Label>
              <Input
                id="new-password"
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                data-testid="new-password-input"
              />
              <p className="text-xs text-slate-500 mt-1">Password will be visible to admin</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPasswordDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handlePasswordUpdate}
                className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white"
                disabled={!newPassword}
                data-testid="update-password-btn"
              >
                Update Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="max-w-md" data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-purple-600" />
              Change Role
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-3">
              <div className="text-sm text-slate-600">
                User: <b className="text-slate-900">{selectedUser.email}</b>
                <div className="text-xs text-slate-500 mt-0.5">Current role: <b>{selectedUser.role}</b></div>
              </div>
              <div>
                <Label>New Role *</Label>
                <Select value={roleForm.role} onValueChange={(v) => setRoleForm({ ...roleForm, role: v })}>
                  <SelectTrigger data-testid="role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Admin">Admin (full access)</SelectItem>
                    <SelectItem value="Branch">Branch (limited to home branch)</SelectItem>
                    <SelectItem value="Checker">Checker (review only, one branch)</SelectItem>
                    <SelectItem value="Account">Account (finance modules)</SelectItem>
                    <SelectItem value="HR">HR (staff & training)</SelectItem>
                    <SelectItem value="CRP-EP">CRP-EP (Business Plans & SVEP)</SelectItem>
                    <SelectItem value="CentreManager">Centre Manager (Inmates & Centre Staff)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {['Branch', 'Checker'].includes(roleForm.role) && (
                <div>
                  <Label>Home Branch *</Label>
                  <Select value={roleForm.branch_id} onValueChange={(v) => setRoleForm({ ...roleForm, branch_id: v })}>
                    <SelectTrigger data-testid="role-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {!['Branch', 'Checker'].includes(roleForm.role) && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  Switching to a non-branch role will clear this user&apos;s home branch and any extra allowed branches.
                </p>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
                <Button onClick={saveRole} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-role-btn">
                  Save Role
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Branch Access Dialog */}
      <Dialog open={branchAccessDialogOpen} onOpenChange={setBranchAccessDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-emerald-700" />
              Manage Branch Access
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500">User</p>
              <p className="font-medium text-slate-900">{selectedUser?.email}</p>
              <p className="text-xs text-slate-500 mt-1">
                Home Block: <b>{branches.find(b => b.id === selectedUser?.branch_id)?.name || selectedUser?.branch_name || '—'}</b>
              </p>
            </div>
            <div>
              <Label className="mb-2 block">Additional blocks this mentor can enter expenditure for:</Label>
              <div className="max-h-64 overflow-y-auto border rounded-lg p-2 space-y-1 bg-white">
                {branches.filter(b => b.id !== selectedUser?.branch_id).length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">No other blocks available</p>
                ) : (
                  branches
                    .filter(b => b.id !== selectedUser?.branch_id)
                    .map((b) => (
                      <label
                        key={b.id}
                        className="flex items-center gap-2 px-2 py-1.5 hover:bg-emerald-50 rounded cursor-pointer text-sm"
                        data-testid={`access-branch-${b.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={accessSelectedIds.includes(b.id)}
                          onChange={() => toggleAccessBranch(b.id)}
                          className="w-4 h-4 accent-emerald-600"
                        />
                        <span className="font-mono text-xs text-slate-500">{b.code}</span>
                        <span className="text-slate-800">{b.name}</span>
                      </label>
                    ))
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Selected: <b>{accessSelectedIds.length}</b> additional {accessSelectedIds.length === 1 ? 'block' : 'blocks'}
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setBranchAccessDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={saveBranchAccess}
                className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="save-branch-access-btn"
              >
                Save Access
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
